import { useParams, useNavigate } from "react-router-dom";
import { Clock, MapPin, Users, ArrowLeft, MessageCircle, Share2 } from "lucide-react";
import { MOCK_ACTIVITIES, formatTimeFromNow, getActivityType, getIntentLabel, getVibeLabel, INTENTS, VIBES } from "@/lib/mockData";
import MatchScoreBadge from "@/components/MatchScoreBadge";
import { useAuth } from "@/lib/AuthContext";
import { apiInvites } from "@/lib/api";
import { toast } from "sonner";
import { useEffect, useState } from "react";

const ActivityDetail = () => {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [activity, setActivity] = useState(null);
  const [joined, setJoined] = useState(false);

  useEffect(() => {
    apiInvites.get(id).then((d) => {
      setActivity(d);
      setJoined(!!(user && (d.participants || []).some((p) => p.user_id === user.user_id)));
    }).catch(() => {
      // Fall back to mock for IDs that don't exist in DB (e.g. layout demos)
      const fallback = MOCK_ACTIVITIES.find((a) => a.id === id) || MOCK_ACTIVITIES[0];
      setActivity(fallback);
    });
  }, [id, user]);

  if (!activity) return <div className="pt-10 text-center text-sm text-[#555]">Loading…</div>;
  const type = getActivityType(activity.type);
  const isFull = (activity.participants || []).length >= activity.max_participants;

  const handleJoin = async () => {
    if (!user) {
      window.dispatchEvent(new CustomEvent("grabb:friction", { detail: { action: "join" } }));
      return;
    }
    try {
      if (joined) {
        await apiInvites.leave(activity.id);
        setJoined(false);
        toast.success("Left activity");
      } else {
        await apiInvites.join(activity.id);
        setJoined(true);
        toast.success("Joined — group chat opened");
      }
      const fresh = await apiInvites.get(activity.id);
      setActivity(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const handleCheckin = async () => {
    if (!user) return;
    try {
      const res = await apiInvites.checkin(activity.id);
      toast.success(`Checked in — show-up ${res.show_up_score} (${res.consecutive_show_ups}/5)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Check-in failed");
    }
  };

  const handleComplete = async () => {
    if (!user) return;
    try {
      await apiInvites.complete(activity.id);
      toast.success("Activity wrapped — crew updated");
      const fresh = await apiInvites.get(activity.id);
      setActivity(fresh);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Complete failed");
    }
  };

  const isHost = user && activity.host && (activity.host.user_id === user.user_id);
  const isCompleted = activity.status === "completed";

  // Normalise viewer's intents/vibes (handle legacy string + missing).
  const myIntents = Array.isArray(user?.default_intent) ? user.default_intent : (user?.default_intent ? [user.default_intent] : []);
  const myVibes = Array.isArray(user?.default_vibe) ? user.default_vibe : (user?.default_vibe ? [user.default_vibe] : []);
  const intentLabel = (id) => INTENTS.find((i) => i.id === id)?.label || id;
  const vibeLabel = (id) => VIBES.find((v) => v.id === id)?.label || id;

  const sharedSignals = (p) => {
    if (!user || p.user_id === user.user_id) return [];
    const pIntents = Array.isArray(p.default_intent) ? p.default_intent : (p.default_intent ? [p.default_intent] : []);
    const pVibes = Array.isArray(p.default_vibe) ? p.default_vibe : (p.default_vibe ? [p.default_vibe] : []);
    const sharedI = myIntents.filter((x) => pIntents.includes(x));
    const sharedV = myVibes.filter((x) => pVibes.includes(x));
    return [...sharedI.map((id) => ({ kind: "intent", id, label: intentLabel(id) })),
            ...sharedV.map((id) => ({ kind: "vibe", id, label: vibeLabel(id) }))];
  };

  return (
    <div data-testid="activity-detail-page">
      {/* Hero */}
      <div className="relative -mx-4 mt-4">
        <img src={activity.image} alt={activity.title} className="w-full h-64 object-cover border-b-2 border-[#0A0A0A]" />
        <button
          onClick={() => nav(-1)}
          className="absolute top-4 left-4 w-10 h-10 brutal-border rounded-full bg-white flex items-center justify-center brutal-shadow-sm"
          data-testid="activity-back"
        >
          <ArrowLeft className="w-4 h-4" />
        </button>
        <button
          className="absolute top-4 right-4 w-10 h-10 brutal-border rounded-full bg-white flex items-center justify-center brutal-shadow-sm"
          data-testid="activity-share"
        >
          <Share2 className="w-4 h-4" />
        </button>
        <div className="absolute bottom-3 left-3 flex gap-2">
          <span className="brutal-border bg-white rounded-full px-3 py-1 text-xs font-bold uppercase">
            {type.emoji} {type.label}
          </span>
          {activity.spontaneous && (
            <span className="brutal-border bg-[#FF6A2C] text-white rounded-full px-3 py-1 text-xs font-bold uppercase">
              Spontaneous
            </span>
          )}
        </div>
      </div>

      {/* Title + host */}
      <div className="mt-5">
        <div className="flex items-start justify-between gap-3 mb-3">
          <h1 className="font-heading font-black text-3xl leading-tight">{activity.title}</h1>
          <MatchScoreBadge score={activity.match_score} />
        </div>
        <p className="text-[#555555] mb-4">{activity.description}</p>

        {/* Host card */}
        <div className="brutal-card p-4 flex items-center gap-3">
          <img src={activity.host.avatar_url || activity.host.avatar || "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120"} alt={activity.host.name} className="w-12 h-12 rounded-full brutal-border object-cover" />
          <div>
            <div className="label-mono text-[#555555]">Host</div>
            <div className="font-heading font-bold text-lg">{activity.host.name}{activity.host.age ? `, ${activity.host.age}` : ""}</div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-2 mt-4">
        <BigStat icon={Clock} label="Starts" value={formatTimeFromNow(activity.time_iso)} />
        <BigStat icon={MapPin} label="Distance" value={`${activity.distance_km ?? 0} km`} />
        <BigStat icon={Users} label="Spots" value={`${(activity.participants || []).length}/${activity.max_participants}`} />
      </div>

      {/* Intent + Vibe */}
      <div className="grid grid-cols-2 gap-3 mt-4">
        <div className="brutal-card p-4">
          <div className="label-mono mb-1">Intent</div>
          <div className="font-bold">{getIntentLabel(activity.intent)}</div>
        </div>
        <div className="brutal-card p-4">
          <div className="label-mono mb-1">Vibe</div>
          <div className="font-bold">{getVibeLabel(activity.vibe)}</div>
        </div>
      </div>

      {/* Match reasons */}
      <div className="brutal-card p-5 mt-4 bg-[#FFFBEB]">
        <div className="label-mono mb-3">Why this matches you</div>
        <ul className="space-y-2">
          {(activity.match_reasons || []).map((r, i) => (
            <li key={i} className="flex items-start gap-2 text-sm">
              <span className="mt-1 w-1.5 h-1.5 bg-[#0A0A0A] rounded-full shrink-0" />
              {r}
            </li>
          ))}
        </ul>
      </div>

      {/* Participants */}
      <div className="mt-5">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-bold text-xl">Who&apos;s going</h3>
          <span className="text-xs text-[#555555]">{(activity.participants || []).length} joined</span>
        </div>
        <div className="brutal-card p-4 space-y-3">
          {(activity.participants || []).map((p) => {
            const shared = sharedSignals(p);
            return (
              <div key={p.user_id || p.id} className="flex flex-col gap-1.5">
                <div className="flex items-center gap-3">
                  <img src={p.avatar_url || p.avatar || "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=120"} className="w-9 h-9 rounded-full brutal-border object-cover" alt={p.name} />
                  <div className="flex-1">
                    <div className="font-bold text-sm">{p.name}{p.age ? `, ${p.age}` : ""}</div>
                    <div className="text-xs text-[#555555]">{p.suburb || ""}</div>
                  </div>
                  {typeof p.show_up_score === "number" && (
                    <span
                      className={`brutal-border rounded-full px-2 py-0.5 text-[10px] font-bold ${p.show_up_score >= 80 ? "bg-[#C8E6C9]" : p.show_up_score >= 50 ? "bg-[#FFCC00]" : "bg-[#FF6A2C] text-white"}`}
                      data-testid={`participant-show-up-${p.user_id}`}
                      title="Show-up reliability"
                    >
                      ✅ {p.show_up_score}
                    </span>
                  )}
                </div>
                {shared.length > 0 && (
                  <div className="flex flex-wrap gap-1 pl-12" data-testid={`participant-shared-${p.user_id}`}>
                    <span className="text-[10px] font-mono text-[#555555] uppercase tracking-wide pt-0.5">You both:</span>
                    {shared.map((s) => (
                      <span
                        key={`${s.kind}-${s.id}`}
                        className={`brutal-border rounded-full px-2 py-0.5 text-[10px] font-bold ${s.kind === "intent" ? "bg-[#0A0A0A] text-white" : "bg-[#FFCC00]"}`}
                        title={s.kind === "intent" ? "Shared intent" : "Shared vibe"}
                      >
                        {s.kind === "intent" ? "🎯" : "✨"} {s.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Venue + Deal */}
      {activity.venue && (
        <div className="brutal-card p-4 mt-5">
          <div className="label-mono text-[#555555] mb-1">Venue</div>
          <div className="font-heading font-bold text-lg">{activity.venue.name}</div>
          <div className="text-sm text-[#555555]">{activity.address}</div>
          {activity.deal && (
            <div className="mt-3 inline-block brutal-border bg-[#FFCC00] rounded-md px-3 py-1 font-bold text-xs uppercase">
              {activity.deal.label}
            </div>
          )}
        </div>
      )}

      {/* Sticky CTA */}
      <div className="fixed bottom-16 left-0 right-0 bg-white/95 backdrop-blur-md border-t-2 border-[#0A0A0A] px-4 py-3 z-50">
        <div className="max-w-2xl mx-auto flex gap-3 items-center">
          <button
            onClick={() => nav(`/chats/${activity.id}`)}
            className="brutal-btn bg-white px-4 py-3"
            data-testid="activity-open-chat"
          >
            <MessageCircle className="w-4 h-4" />
          </button>
          {joined && !isHost && !isCompleted && (
            <button onClick={handleCheckin} className="brutal-btn bg-[#C8E6C9] px-3 py-3 text-xs font-bold" data-testid="activity-checkin">
              ✓ Check in
            </button>
          )}
          {isHost && !isCompleted ? (
            <button onClick={handleComplete} className="flex-1 brutal-btn bg-[#0A0A0A] text-white py-3" data-testid="activity-complete-btn">
              Wrap & update crew
            </button>
          ) : (
            <button
              onClick={handleJoin}
              disabled={(isFull && !joined) || isCompleted}
              className={`flex-1 brutal-btn py-3 ${
                isCompleted
                  ? "bg-[#F8F9FA] text-[#999]"
                  : isFull && !joined
                  ? "bg-[#F8F9FA] text-[#999]"
                  : joined
                  ? "bg-white text-[#0A0A0A]"
                  : "bg-[#FF6A2C] text-white"
              }`}
              data-testid="activity-join-btn"
            >
              {isCompleted ? "Completed" : isFull && !joined ? "Full" : joined ? "Leave activity" : "Join activity"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const BigStat = ({ icon: Icon, label, value }) => (
  <div className="brutal-card p-3 text-center">
    <Icon className="w-4 h-4 mx-auto mb-1" strokeWidth={2.5} />
    <div className="font-heading font-black text-lg leading-none">{value}</div>
    <div className="label-mono text-[#555555] mt-1">{label}</div>
  </div>
);

export default ActivityDetail;
